import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ManufacturingLocationSelection from './ManufacturingLocationSelection';
import { useManufacturingLocations } from '../utils';
import { ManufacturingLocation } from '../types';
import GenericDialog from '../../common/GenericDialog';
import { EMPTY_GUID } from '../../../utils/string-utils';

type Props = {
  initialManufacturingLocation: ManufacturingLocation;
  open: boolean;
  onConfirm: (manufacturingLocation: ManufacturingLocation) => void;
  onCancel: () => void;
  confirmDisabled?: boolean;
  disableMCellCollection?: boolean;
  includeRollupCells?: boolean;
  removeInventoryCenterAllOption?: boolean;
  removeMCellAllOption?: Boolean;
  excludedMCellIds?: string[];
};

/**
 * Renders ManufacturingLocationSelection component in a dialog
 * @param initialManufacturingLocation
 * @param open
 * @param onConfirm
 * @param onCancel
 * @param confirmDisabled
 * @param disableMCellCollection
 * @param includeRollupCells
 * @param removeInventoryCenterAllOption
 * @param removeMCellAllOption
 * @param excludedMCellIds - array of MCell IDs to exclude from the selection
 * @constructor
 */
const ManufacturingLocationSelectionDialog = ({
  initialManufacturingLocation,
  open,
  onConfirm,
  onCancel,
  confirmDisabled,
  disableMCellCollection,
  includeRollupCells,
  removeInventoryCenterAllOption,
  removeMCellAllOption,
  excludedMCellIds = [],
}: Props) => {
  const { t } = useTranslation();
  const [manufacturingLocation, setManufacturingLocation] = useState(
    initialManufacturingLocation,
  );
  const [componentId, setComponentId] = useState(EMPTY_GUID);
  const prevOpenRef = useRef(open);
  const dialogOpenKeyRef = useRef(0);
  const { mCells, inventoryCenters, mCellCollections } =
    useManufacturingLocations(includeRollupCells);

  const filteredMCells = useMemo(() => {
    if (excludedMCellIds.length === 0) return mCells;

    return mCells.filter(
      (mCell) =>
        mCell.id === EMPTY_GUID || // Always allow "All mCells"
        !excludedMCellIds.includes(mCell.id),
    );
  }, [mCells, excludedMCellIds]);

  /**
   * Effect that sets the initial manufacturing location of the dialog
   */
  useEffect(() => {
    if (open && !prevOpenRef.current) {
      setManufacturingLocation(initialManufacturingLocation);
      setComponentId(EMPTY_GUID);
      dialogOpenKeyRef.current += 1;
    }
    prevOpenRef.current = open;
  }, [open, initialManufacturingLocation]);

  function handleChange(
    manufacturingLocation: ManufacturingLocation,
    componentId: string,
  ) {
    setManufacturingLocation(manufacturingLocation);
    setComponentId(componentId);
  }

  return (
    <GenericDialog
      title={t('setManufacturingLocation')}
      open={open}
      onConfirm={() =>
        onConfirm({
          inventoryCenterId: manufacturingLocation.inventoryCenterId,
          mCellCollectionId: manufacturingLocation.mCellCollectionId,
          mCellId:
            componentId === EMPTY_GUID
              ? manufacturingLocation.mCellId
              : componentId,
        })
      }
      onCancel={onCancel}
      confirmDisabled={confirmDisabled}
      enableBackdropBlur
    >
      <ManufacturingLocationSelection
        key={dialogOpenKeyRef.current}
        mCells={filteredMCells}
        inventoryCenters={inventoryCenters}
        mCellCollections={disableMCellCollection ? undefined : mCellCollections}
        initialManufacturingLocation={initialManufacturingLocation}
        onChange={handleChange}
        removeInventoryCenterAllOption={removeInventoryCenterAllOption}
        removeMCellAllOption={removeMCellAllOption}
      />
    </GenericDialog>
  );
};

export default ManufacturingLocationSelectionDialog;
